import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";
import {
  Clock, Sparkles, Paperclip, CalendarDays, PauseCircle,
  ChevronLeft, ChevronRight, ArrowRight, Activity,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slow enough to read a slide, quick enough that the band reads as alive.
const ADVANCE_MS = 6000;

// One palette per kind of slide, applied to the heading, the rail and the
// tint together so the band reads as one coherent signal.
//
// The quiet tone is a soft amber, not a red. There is almost always a project
// past the threshold, so this colour is on screen most days: it has to read as
// "worth a look" rather than "something is broken", or people stop seeing it.
const TONES = {
  quiet: {
    labelKey: "feed.headingQuiet",
    icon: Clock,
    heading: "text-amber-700",
    wrap: "bg-amber-50/70 border-amber-200",
    rail: "bg-gradient-to-b from-amber-300 to-amber-500",
    title: "text-amber-900",
    sub: "text-amber-700",
    pill: "bg-amber-500 text-white",
  },
  fresh: {
    labelKey: "feed.headingNew",
    icon: Sparkles,
    heading: "text-emerald-700",
    wrap: "bg-emerald-50 border-emerald-200",
    rail: "bg-gradient-to-b from-emerald-400 to-emerald-600",
    title: "text-emerald-950",
    sub: "text-emerald-700",
    pill: "bg-emerald-600 text-white",
  },
  normal: {
    labelKey: "feed.headingLatest",
    icon: Activity,
    heading: "text-sky-700",
    wrap: "bg-sky-50 border-sky-200",
    rail: "bg-gradient-to-b from-sky-400 to-sky-600",
    title: "text-sky-950",
    sub: "text-sky-700",
    pill: "bg-sky-600 text-white",
  },
};

// Past thirty days the relative form stops meaning anything and a date is
// shown instead. It follows the language chosen in Core: passing undefined
// here hands the decision to the operating system, which is how an Italian
// page ends up writing "Aug 27". The feed reaches this routinely, since the
// most recent activity on a quiet project can be months old.
function timeAgo(iso, s, language) {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return s("feed.justNow");
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return s("feed.yesterday");
  if (days < 30) return `${days}d`;
  return then.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
    day: "numeric",
    month: "short",
  });
}

function clip(text, max = 70) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max)}\u2026` : clean;
}

export default function Feed() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const s = localStrings(language);

  const [data, setData] = useState(null);
  const [paused, setPaused] = useState(false);
  const [active, setActive] = useState(0);
  const scroller = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/feed`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => setData(false));
  }, []);

  const projectSlides = data && data.slides ? data.slides : [];
  const quiet = data && data.quiet ? data.quiet : [];

  // Everything that has gone quiet travels as a single closing card, after
  // the things that actually happened.
  const cards = [
    ...projectSlides.map((sl) => ({ kind: sl.is_new ? "fresh" : "normal", slide: sl })),
    ...(quiet.length > 0 ? [{ kind: "quiet", quiet }] : []),
  ];

  const goTo = useCallback((index) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * index, behavior: "smooth" });
  }, []);

  const step = useCallback(
    (direction) => {
      if (cards.length === 0) return;
      const next = (active + direction + cards.length) % cards.length;
      setActive(next);
      goTo(next);
    },
    [active, cards.length, goTo]
  );

  useEffect(() => {
    if (paused || cards.length < 2) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const timer = setInterval(() => step(1), ADVANCE_MS);
    return () => clearInterval(timer);
  }, [paused, cards.length, step]);

  // Keep the heading and dots honest when the band is swiped by hand.
  const onScroll = () => {
    const el = scroller.current;
    if (!el || el.clientWidth === 0) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    if (index !== active) setActive(index);
  };

  // A failing feed must not take the home page with it.
  if (data === false) return null;

  if (data === null) {
    return <div className="h-full min-h-[220px] rounded-2xl bg-slate-100 animate-pulse" />;
  }

  if (cards.length === 0) {
    return (
      <div
        className="h-full flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white/60 py-10 text-center"
        data-testid="feed-empty"
      >
        <p className="text-sm font-medium text-slate-600">{s("feed.empty")}</p>
        <p className="text-xs text-slate-400 mt-1">{s("feed.emptyHint")}</p>
      </div>
    );
  }

  // The heading describes the card currently on screen.
  const activeTone = TONES[cards[active]?.kind] || TONES.normal;
  const HeadingIcon = activeTone.icon;

  return (
    // A column filling its grid cell, so the card grows to whatever height the
    // row settles on rather than leaving a gap under itself next to the
    // calendar.
    //
    // A touch pauses the advance and the end of that touch releases it. A
    // phone never fires onMouseLeave, so with only the start of a touch
    // handled the first tap stopped the band for the rest of the page's life.
    <section
      data-testid="feed"
      className="flex flex-col h-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      onTouchCancel={() => setPaused(false)}
    >
      {/* Fixed-height heading row. The arrows are 28px and the label is 16px,
          so without a set height this row was taller than the calendar's
          heading beside it and the two labels sat on different lines. */}
      <div className="flex items-center justify-between mb-2 h-7 shrink-0">
        <span
          data-testid="feed-heading"
          className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest transition-colors duration-500 ${activeTone.heading}`}
        >
          <HeadingIcon className="w-3.5 h-3.5" />
          {s(activeTone.labelKey)}
        </span>
        {cards.length > 1 && (
          <div className="flex gap-1">
            <button
              onClick={() => step(-1)}
              aria-label={s("feed.previous")}
              data-testid="feed-prev"
              className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => step(1)}
              aria-label={s("feed.next")}
              data-testid="feed-next"
              className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* No rounding on the scroller itself. It used to carry rounded-2xl,
          which clips its contents to that shape, and since each card is
          exactly as wide as the container, the clip shaved the left and right
          borders off, most visibly mid-scroll. The cards are already rounded;
          the container does not need to be. */}
      <div
        ref={scroller}
        onScroll={onScroll}
        className="flex flex-1 min-h-0 overflow-x-auto snap-x snap-mandatory"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {cards.map((card, idx) => {
          const tone = TONES[card.kind];

          // --- The closing summary of everything that has gone quiet -------
          if (card.kind === "quiet") {
            return (
              <div
                key="quiet-summary"
                data-testid="feed-quiet-summary"
                className={`shrink-0 w-full snap-start border rounded-2xl ${tone.wrap} relative overflow-hidden`}
              >
                <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${tone.rail}`} />
                <div className="p-5 pl-6">
                  <p className={`text-sm font-semibold ${tone.title} mb-3`}>
                    {s("feed.quietSummary", { count: card.quiet.length })}
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {card.quiet.slice(0, 8).map((q) => (
                      <li key={q.project_id}>
                        <button
                          onClick={() => navigate(`/projects/${q.project_id}`)}
                          data-testid={`feed-quiet-${q.project_id}`}
                          className="w-full text-left flex items-baseline gap-2 min-w-0 group"
                        >
                          <span className="text-sm text-slate-800 truncate group-hover:underline">
                            {q.project_title}
                          </span>
                          {q.waiting_for && (
                            <PauseCircle className="w-3 h-3 text-amber-600 shrink-0 self-center" />
                          )}
                          <span className="text-xs text-amber-700 ml-auto shrink-0 tabular-nums">
                            {s("proj.silentDays", { days: q.silence_days })}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {card.quiet.length > 8 && (
                    <p className="text-xs text-amber-700/70 mt-2">
                      +{card.quiet.length - 8}
                    </p>
                  )}
                </div>
              </div>
            );
          }

          // --- One project with recent activity ----------------------------
          const sl = card.slide;
          return (
            <div
              key={sl.project_id}
              data-testid={`feed-slide-${sl.project_id}`}
              className={`shrink-0 w-full snap-start border rounded-2xl ${tone.wrap} relative overflow-hidden`}
            >
              <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${tone.rail}`} />

              <div className="grid md:grid-cols-5 gap-4 p-5 pl-6">
                <div className="md:col-span-2 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {sl.is_new && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone.pill}`}>
                        <Sparkles className="w-3 h-3" />
                        {s("feed.newProject")}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => navigate(`/projects/${sl.project_id}`)}
                    data-testid={`feed-open-${sl.project_id}`}
                    className={`text-xl font-bold leading-tight ${tone.title} hover:underline text-left truncate max-w-full block`}
                  >
                    {sl.project_title}
                  </button>

                  {typeof sl.silence_days === "number" && (
                    <p className={`text-xs mt-0.5 ${tone.sub}`}>
                      {sl.silence_days === 0
                        ? s("proj.today")
                        : s("proj.silentDays", { days: sl.silence_days })}
                    </p>
                  )}

                  {sl.waiting_for && (
                    <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-white/70 border border-black/5 px-2.5 py-1.5">
                      <PauseCircle className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
                      <span className="text-xs text-slate-700 leading-snug">
                        <span className="text-slate-500">{s("feed.waitingOn")}:</span>{" "}
                        {sl.waiting_for}
                      </span>
                    </div>
                  )}

                  {sl.people && sl.people.length > 0 && (
                    <div className="flex -space-x-1.5 mt-3">
                      {sl.people.slice(0, 5).map((p) => (
                        <span
                          key={p.id}
                          title={p.display_name}
                          className="w-6 h-6 rounded-full ring-2 ring-white text-white text-[10px] font-semibold flex items-center justify-center"
                          style={{ backgroundColor: p.color || "#3730A3" }}
                        >
                          {(p.display_name || "?").charAt(0).toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="md:col-span-3 min-w-0 md:border-l md:border-black/5 md:pl-5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {s("feed.recentActivity")}
                    </span>
                    <button
                      onClick={() => navigate(`/projects/${sl.project_id}`)}
                      className="text-[11px] font-medium text-indigo-600 hover:underline inline-flex items-center gap-0.5"
                    >
                      {s("feed.openProject")}
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>

                  {sl.updates && sl.updates.length > 0 ? (
                    <ul className="space-y-1.5">
                      {sl.updates.map((u, i) => (
                        <li key={i} className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-5 h-5 rounded-full shrink-0 text-white text-[10px] font-semibold flex items-center justify-center"
                            style={{ backgroundColor: u.author_color || "#3730A3" }}
                          >
                            {(u.author_name || "?").charAt(0).toUpperCase()}
                          </span>
                          <span className="text-sm text-slate-700 truncate">{clip(u.content)}</span>
                          {u.is_meeting && (
                            <CalendarDays className="w-3 h-3 text-slate-300 shrink-0" />
                          )}
                          {u.has_attachment && (
                            <Paperclip className="w-3 h-3 text-slate-300 shrink-0" />
                          )}
                          <span className="text-[11px] text-slate-400 ml-auto shrink-0">
                            {timeAgo(u.at, s, language)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 py-2">{s("feed.noUpdatesYet")}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {cards.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2.5 shrink-0">
          {cards.map((card, i) => (
            <button
              key={card.kind === "quiet" ? "quiet" : card.slide.project_id}
              onClick={() => {
                setActive(i);
                goTo(i);
              }}
              data-testid={`feed-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === active ? "w-5 bg-slate-600" : "w-1.5 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
